/**
 * Terpene Store - Zustand store for terpene browser
 * Manages terpene data fetching and modal state
 */

import { create } from 'zustand'
import { terpeneApi } from '@/lib/api/terpenes/supabase-client'
import type { TerpeneRow } from '@/types/supabase'

interface TerpeneStore {
  // Modal state
  isModalOpen: boolean
  openModal: () => void
  closeModal: () => void
  
  // Data state
  terpenes: TerpeneRow[]
  filteredTerpenes: TerpeneRow[]
  selectedTerpene: TerpeneRow | null
  categories: string[]
  effects: string[]
  sources: string[]
  
  // Filter state
  searchQuery: string
  selectedCategory: string | null
  selectedEffects: string[]
  selectedSources: string[]
  
  // Loading state
  isLoading: boolean
  error: string | null
  
  // Actions
  fetchTerpenes: () => Promise<void>
  fetchFilterOptions: () => Promise<void>
  setSearchQuery: (query: string) => void
  setSelectedCategory: (category: string | null) => void
  setSelectedEffects: (effects: string[]) => void
  setSelectedSources: (sources: string[]) => void
  selectTerpene: (terpene: TerpeneRow) => void
  applyFilters: () => Promise<void>
  clearFilters: () => void
}

export const useTerpeneStore = create<TerpeneStore>((set, get) => ({
  // Modal state
  isModalOpen: false,
  openModal: () => {
    set({ isModalOpen: true })
    // Fetch data when modal opens
    const state = get()
    if (state.terpenes.length === 0) {
      state.fetchTerpenes()
      // fetchFilterOptions is now called automatically after fetchTerpenes
    }
  },
  closeModal: () => set({ isModalOpen: false }),
  
  // Data state
  terpenes: [],
  filteredTerpenes: [],
  selectedTerpene: null,
  categories: [],
  effects: [],
  sources: [],
  
  // Filter state
  searchQuery: '',
  selectedCategory: null,
  selectedEffects: [],
  selectedSources: [],
  
  // Loading state
  isLoading: false,
  error: null,
  
  // Actions
  fetchTerpenes: async () => {
    set({ isLoading: true, error: null })
    try {
      const data = await terpeneApi.list()
      set({ 
        terpenes: data,
        filteredTerpenes: data,
        selectedTerpene: data[0] || null,
        isLoading: false 
      })
      
      // After loading terpenes, extract filter options from the data
      get().fetchFilterOptions()
    } catch (error) {
      console.error('Failed to fetch terpenes:', error)
      set({ 
        error: 'Failed to load terpenes. Please try again.',
        isLoading: false 
      })
    }
  },
  
  fetchFilterOptions: async () => {
    try {
      const state = get()
      
      // Extract categories from loaded terpenes for better reliability
      const categoriesSet = new Set<string>()
      const effectsSet = new Set<string>()
      const sourcesSet = new Set<string>()
      
      state.terpenes.forEach(terpene => {
        // Collect categories
        if (terpene.category && typeof terpene.category === 'string') {
          categoriesSet.add(terpene.category)
        }
        
        // Collect effects
        if (terpene.primary_effects && Array.isArray(terpene.primary_effects)) {
          terpene.primary_effects.forEach(effect => {
            if (effect && typeof effect === 'string') {
              effectsSet.add(effect)
            }
          })
        }
        
        // Collect sources
        if (terpene.natural_sources && Array.isArray(terpene.natural_sources)) {
          terpene.natural_sources.forEach(source => {
            if (source && typeof source === 'string') {
              sourcesSet.add(source)
            }
          })
        }
      })
      
      // Convert to sorted arrays
      const categories = Array.from(categoriesSet).sort()
      const effects = Array.from(effectsSet).sort()
      const sources = Array.from(sourcesSet).sort()
      
      set({
        categories: categories.length > 0 ? categories : ['Monoterpene', 'Sesquiterpene', 'Diterpene', 'Triterpene'],
        effects: effects.slice(0, 15), // Show top 15 effects
        sources: sources.slice(0, 15)  // Show top 15 sources
      })
    } catch (error) {
      console.error('Failed to load filter options:', error)
      // Set defaults if loading fails
      set({
        categories: ['Monoterpene', 'Sesquiterpene', 'Diterpene', 'Triterpene'],
        effects: ['anti-inflammatory', 'antimicrobial', 'antioxidant', 'analgesic', 'sedative'],
        sources: ['Cannabis', 'Citrus Fruits', 'Pine', 'Lavender', 'Black Pepper']
      })
    }
  },
  
  setSearchQuery: (query: string) => {
    set({ searchQuery: query })
    get().applyFilters()
  },
  
  setSelectedCategory: (category: string | null) => {
    set({ selectedCategory: category })
    get().applyFilters()
  },
  
  setSelectedEffects: (effects: string[]) => {
    set({ selectedEffects: effects })
    get().applyFilters()
  },
  
  setSelectedSources: (sources: string[]) => {
    set({ selectedSources: sources })
    get().applyFilters()
  },
  
  selectTerpene: (terpene: TerpeneRow) => {
    set({ selectedTerpene: terpene })
  },
  
  applyFilters: async () => {
    const state = get()
    
    // Apply client-side filtering for better reliability
    let filtered = [...state.terpenes]
    
    // Search filter - check name and description
    if (state.searchQuery) {
      const query = state.searchQuery.toLowerCase()
      filtered = filtered.filter(t => 
        t.name?.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query)
      )
    }
    
    // Category filter - case-insensitive match
    if (state.selectedCategory) {
      filtered = filtered.filter(t => 
        t.category?.toLowerCase() === state.selectedCategory.toLowerCase()
      )
    }
    
    // Effects filter - check if terpene has ANY of the selected effects
    if (state.selectedEffects.length > 0) {
      filtered = filtered.filter(t => {
        if (!t.primary_effects || !Array.isArray(t.primary_effects)) return false
        
        // Check if the terpene has at least one of the selected effects
        return state.selectedEffects.some(selectedEffect => {
          // Case-insensitive partial match
          const effectLower = selectedEffect.toLowerCase()
          return t.primary_effects?.some(terpeneEffect => 
            terpeneEffect.toLowerCase().includes(effectLower)
          )
        })
      })
    }
    
    // Sources filter - check if terpene has ANY of the selected sources
    if (state.selectedSources.length > 0) {
      filtered = filtered.filter(t => {
        if (!t.natural_sources || !Array.isArray(t.natural_sources)) return false
        
        // Check if the terpene has at least one of the selected sources
        return state.selectedSources.some(selectedSource => {
          // Case-insensitive partial match
          const sourceLower = selectedSource.toLowerCase()
          return t.natural_sources?.some(terpeneSource => 
            terpeneSource.toLowerCase().includes(sourceLower)
          )
        })
      })
    }
    
    set({ 
      filteredTerpenes: filtered
    })
    
    // Update selected terpene if it's not in filtered results
    if (state.selectedTerpene && !filtered.find(t => t.id === state.selectedTerpene?.id)) {
      set({ selectedTerpene: filtered[0] || null })
    }
  },
  
  clearFilters: () => {
    const state = get()
    set({
      searchQuery: '',
      selectedCategory: null,
      selectedEffects: [],
      selectedSources: [],
      filteredTerpenes: state.terpenes,
      selectedTerpene: state.terpenes[0] || null
    })
  }
}))